/**
 * Three transient phases the Summary page can be in before the report renders:
 * - Loading (initial fetch)
 * - Generating (LLM in flight; polled every 2s)
 * - Failed (LLM generation failed; show retry)
 */
import { Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LogoSpinner from "@/components/LogoSpinner";

export function LoadingPhase() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-24 flex flex-col items-center justify-center">
        <LogoSpinner
          size={96}
          label="Loading your continuity summary…"
          sublabel="Just a moment while we pull everything together."
        />
      </main>
      <Footer />
    </div>
  );
}

export function GeneratingPhase({ elapsedSeconds }) {
  const tip = elapsedSeconds < 12
    ? "Reviewing your answers and surfacing the operational signals."
    : elapsedSeconds < 35
      ? "Drafting risk scenarios tailored to your industry and country."
      : elapsedSeconds < 75
        ? "Shaping continuity strategies and incident playbooks."
        : "Final passes on recommendations and recovery priorities.";
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-20 flex flex-col items-center" data-testid="summary-generating">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Drafting your plan</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight text-center">
          Your resilience plan is being assembled
        </h1>

        <div className="mt-12">
          <LogoSpinner
            size={140}
            label={tip}
            sublabel={`Elapsed: ${elapsedSeconds}s · this typically takes 60–120 seconds.`}
          />
        </div>

        <div className="mt-12 w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900 p-6">
          <div className="flex items-start gap-3">
            <Sparkles className="h-4 w-4 text-teal-700 dark:text-teal-400 mt-1 shrink-0" />
            <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
              We're tailoring scenarios and recovery steps to your mode, industry, country, and team size.
              You can safely leave this tab and come back — your plan will be waiting.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function FailedPhase({ onRetry, errorMessage, onEditAnswers }) {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-20" data-testid="summary-failed">
        <div className="rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
              We couldn't finalise your plan
            </p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
              Your answers are saved. Tap retry to try again — most failures are transient.
            </p>
            {errorMessage && (
              <p
                data-testid="summary-failed-error"
                className="mt-3 text-xs text-amber-900 dark:text-amber-200 leading-relaxed font-mono break-words"
              >
                {errorMessage}
              </p>
            )}
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <button
            onClick={onRetry}
            data-testid="summary-retry"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
          >
            Retry generation <ArrowRight className="h-4 w-4" />
          </button>
          {onEditAnswers && (
            <button
              onClick={onEditAnswers}
              data-testid="summary-edit-answers"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Edit my answers
            </button>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
